import { canAccessOrgSettings } from "~/lib/permissions"
import {
	BookOpenIcon,
	BuildingIcon,
	CircleDotIcon,
	FilePlusIcon,
	GitBranchIcon,
	InboxIcon,
	MessageSquareIcon,
	PlusCircleIcon,
	SettingsIcon,
	SirenIcon,
	TagIcon,
	UserIcon,
	UsersIcon,
	WorkflowIcon
} from "lucide-react"

import { usePostHog } from "posthog-js/react"
import {
	type Dispatch,
	type SetStateAction,
	useDeferredValue,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react"
import { useNavigate } from "react-router-dom"
import { Kbd } from "../ui/kbd"
import {
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandShortcut
} from "~/components/ui/command"
import { useOrganization } from "~/hooks/useOrganization"
import { usePermission } from "~/hooks/usePermission"
import { IS_APPLE } from "~/lib/environment"
import { useTeam } from "~/hooks/useTeam"
import {
	appShortcuts,
	displayShortcut,
	isModifiedShortcut,
	type QuickNavigationShortcutId,
	quickNavigationShortcuts,
	shortcutSequenceMatches,
	shortcutSequenceStartsWith
} from "~/lib/keyboard-shortcuts"
import { TeamPermissions } from "~/lib/permissions"
import { enumFormat } from "~/lib/utils"
import { api, type RouterOutputs } from "~/trpc/react"
import { SearchIcon } from "../icons/search"
import { posthogEnabled } from "../providers/Posthog"
import { Button } from "../ui/button"

const resultIcons = {
	issue: WorkflowIcon,
	comment: MessageSquareIcon,
	person: UsersIcon,
	team: UsersIcon,
	organization: BuildingIcon,
	label: TagIcon,
	github_repository: GitBranchIcon,
	knowledge_base_page: BookOpenIcon
} satisfies Record<
	RouterOutputs["search"]["query"][number]["type"],
	typeof WorkflowIcon
>

const resultGroups = [
	"issue",
	"comment",
	"knowledge_base_page",
	"person",
	"team",
	"organization",
	"label",
	"github_repository"
] satisfies RouterOutputs["search"]["query"][number]["type"][]

export function NavSearchTrigger({ onOpen }: { onOpen: () => void }) {
	return (
		<Button
			variant="command"
			onClick={onOpen}
			size="sm"
			shape="rounded"
			className="w-full min-w-0"
		>
			<span className="flex flex-row gap-2 grow">
				<SearchIcon className="size-4 text-icons" />
				<span className="truncate text-xs font-medium">Search</span>
			</span>
			<Kbd variant="plain">{IS_APPLE ? "⌘" : "Ctrl+"}K</Kbd>
		</Button>
	)
}

export function NavSearch({
	open,
	onOpenChange: setOpen
}: {
	open: boolean
	onOpenChange: Dispatch<SetStateAction<boolean>>
}) {
	const [search, setSearch] = useState("")
	const deferredSearch = useDeferredValue(search)
	const shortcutSequence = useRef<string[]>([])
	const shortcutTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
	const lastCapturedSearch = useRef("")
	const modifierKeyDown = useRef(false)
	const navigate = useNavigate()
	const posthog = usePostHog()
	const { organization } = useOrganization()
	const { team } = useTeam()
	const { hasTeamPermission, orgPermissions } = usePermission()
	const canCreateIssue = team
		? hasTeamPermission(TeamPermissions.CREATE_ISSUES) ||
			hasTeamPermission(TeamPermissions.CREATE_QUEUE_ISSUES)
		: !!organization
	const canViewKnowledgeBase =
		!!team && hasTeamPermission(TeamPermissions.VIEW_KNOWLEDGE_BASE)
	const canCreateKnowledgeBasePage =
		!!team && hasTeamPermission(TeamPermissions.EDIT_KNOWLEDGE_BASE)
	const searchQuery = api.search.query.useQuery(
		{
			query: deferredSearch,
			organizationId: organization?.id,
			teamId: team?.id,
			limit: 20
		},
		{
			enabled: open && deferredSearch.trim().length >= 2
		}
	)
	const utils = api.useUtils()
	const createKnowledgeBaseDocument = api.knowledgeBase.create.useMutation({
		onSuccess: async (document) => {
			if (!document || !organization || !team) return
			await utils.knowledgeBase.list.invalidate({ teamId: team.id })
			navigate(`/${organization.slug}/${team.slug}/kb/${document.slug}`)
		}
	})
	const resultsByType = useMemo(() => {
		const grouped = new Map<
			RouterOutputs["search"]["query"][number]["type"],
			RouterOutputs["search"]["query"]
		>()
		const results =
			deferredSearch.trim().length >= 2 ? (searchQuery.data ?? []) : []
		for (const result of results) {
			grouped.set(result.type, [...(grouped.get(result.type) ?? []), result])
		}
		return grouped
	}, [deferredSearch, searchQuery.data])

	useEffect(() => {
		if (!open) setSearch("")
	}, [open])

	useEffect(() => {
		const normalizedSearch = deferredSearch.trim()
		if (
			!posthogEnabled ||
			!posthog ||
			!open ||
			normalizedSearch.length < 2 ||
			searchQuery.isFetching ||
			lastCapturedSearch.current === normalizedSearch
		) {
			return
		}
		lastCapturedSearch.current = normalizedSearch
		posthog.capture("global_search_performed", {
			queryLength: normalizedSearch.length,
			resultCount: searchQuery.data?.length ?? 0,
			organizationId: organization?.id,
			teamId: team?.id
		})
	}, [
		deferredSearch,
		open,
		organization?.id,
		posthog,
		searchQuery.data?.length,
		searchQuery.isFetching,
		team?.id
	])

	const actions: {
		id: QuickNavigationShortcutId
		title: string
		shortcutId: QuickNavigationShortcutId
		icon: typeof PlusCircleIcon
		disabled: boolean
		run: () => void
	}[] = [
		{
			id: "create_issue",
			title: "Create Issue",
			shortcutId: "create_issue",
			icon: PlusCircleIcon,
			disabled: !canCreateIssue,
			run: () => document.dispatchEvent(new Event("orbit:create-issue"))
		},
		{
			id: "create_knowledge_base_page",
			title: "Create Knowledge Base Page",
			shortcutId: "create_knowledge_base_page",
			icon: FilePlusIcon,
			disabled:
				!canCreateKnowledgeBasePage || createKnowledgeBaseDocument.isPending,
			run: () => {
				if (!team) return
				createKnowledgeBaseDocument.mutate({
					teamId: team.id,
					title: "Untitled document"
				})
			}
		},
		{
			id: "go_home",
			title: "Organization Home",
			shortcutId: "go_home",
			icon: BuildingIcon,
			disabled: !organization,
			run: () => {
				if (organization) navigate(`/${organization.slug}`)
			}
		},
		{
			id: "open_inbox",
			title: "My Inbox",
			shortcutId: "open_inbox",
			icon: InboxIcon,
			disabled: !organization,
			run: () => {
				if (organization) navigate(`/${organization.slug}/me/inbox`)
			}
		},
		{
			id: "open_my_issues",
			title: "My Issues",
			shortcutId: "open_my_issues",
			icon: WorkflowIcon,
			disabled: !organization,
			run: () => {
				if (organization) navigate(`/${organization.slug}/me/issues`)
			}
		},
		{
			id: "open_user_settings",
			title: "User Settings",
			shortcutId: "open_user_settings",
			icon: UserIcon,
			disabled: !organization,
			run: () => {
				if (organization) navigate(`/${organization.slug}/me/settings`)
			}
		},
		{
			id: "open_organization_settings",
			title: "Organization Settings",
			shortcutId: "open_organization_settings",
			icon: SettingsIcon,
			disabled: !organization || !canAccessOrgSettings(orgPermissions),
			run: () => {
				if (organization) navigate(`/${organization.slug}/settings`)
			}
		},
		{
			id: "open_team_issues",
			title: "Team Issues",
			shortcutId: "open_team_issues",
			icon: CircleDotIcon,
			disabled: !organization || !team,
			run: () => {
				if (organization && team)
					navigate(`/${organization.slug}/${team.slug}/issues`)
			}
		},
		{
			id: "open_team_queue",
			title: "Team Queue",
			shortcutId: "open_team_queue",
			icon: SirenIcon,
			disabled: !organization || !team?.enableQueue,
			run: () => {
				if (organization && team) {
					navigate(`/${organization.slug}/${team.slug}/queue`)
				}
			}
		},
		{
			id: "open_knowledge_base",
			title: "Knowledge Base",
			shortcutId: "open_knowledge_base",
			icon: BookOpenIcon,
			disabled: !organization || !team || !canViewKnowledgeBase,
			run: () => {
				if (organization && team)
					navigate(`/${organization.slug}/${team.slug}/kb`)
			}
		},
		{
			id: "open_team_settings",
			title: "Team Settings",
			shortcutId: "open_team_settings",
			icon: SettingsIcon,
			disabled: !organization || !team,
			run: () => {
				if (organization && team) {
					navigate(`/${organization.slug}/${team.slug}/settings`)
				}
			}
		}
	]
	const actionSearch = deferredSearch.trim().toLowerCase()
	const visibleActions = actions.filter((action) => {
		if (action.id === "open_organization_settings" && action.disabled)
			return false
		if (!actionSearch) return true
		const shortcut = quickNavigationShortcuts[action.shortcutId]
		return [action.title, displayShortcut(shortcut.keys), ...shortcut.keywords]
			.join(" ")
			.toLowerCase()
			.includes(actionSearch)
	})
	const runAction = (action: (typeof actions)[number]) => {
		if (action.disabled) return
		if (posthogEnabled && posthog) {
			posthog.capture("quick_switcher_action_selected", {
				action: action.id,
				organizationId: organization?.id,
				teamId: team?.id
			})
		}
		setOpen(false)
		requestAnimationFrame(action.run)
	}
	const actionsRef = useRef(actions)
	const runActionRef = useRef(runAction)
	actionsRef.current = actions
	runActionRef.current = runAction

	useEffect(() => {
		const down = (e: KeyboardEvent) => {
			if (["Alt", "Control", "Meta"].includes(e.key)) {
				modifierKeyDown.current = true
				shortcutSequence.current = []
				return
			}

			const hasCommandModifier =
				modifierKeyDown.current ||
				e.metaKey ||
				e.ctrlKey ||
				e.getModifierState("Meta") ||
				e.getModifierState("Control")
			const hasShortcutModifier =
				hasCommandModifier || e.altKey || e.getModifierState("Alt")
			const isSearchShortcut = isModifiedShortcut(
				e,
				appShortcuts.open_search.keys,
				hasCommandModifier
			)

			if (isSearchShortcut) {
				e.preventDefault()
				setOpen((open) => !open)
				return
			}

			if (hasShortcutModifier) {
				shortcutSequence.current = []
				return
			}

			const target = e.target
			if (
				e.key.length !== 1 ||
				(target instanceof HTMLElement &&
					(["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) ||
						target.isContentEditable ||
						!!target.closest('[contenteditable="true"]')))
			) {
				return
			}

			if (shortcutTimeout.current) clearTimeout(shortcutTimeout.current)
			const nextSequence = [...shortcutSequence.current, e.key.toUpperCase()]
			const exactAction = actionsRef.current.find((action) =>
				shortcutSequenceMatches(
					quickNavigationShortcuts[action.shortcutId].keys,
					nextSequence
				)
			)
			const hasLongerMatch = actionsRef.current.some((action) =>
				shortcutSequenceStartsWith(
					quickNavigationShortcuts[action.shortcutId].keys,
					nextSequence
				)
			)

			if (exactAction && !hasLongerMatch) {
				e.preventDefault()
				shortcutSequence.current = []
				runActionRef.current(exactAction)
				return
			}

			if (hasLongerMatch) {
				e.preventDefault()
				shortcutSequence.current = nextSequence
				shortcutTimeout.current = setTimeout(() => {
					shortcutSequence.current = []
				}, 1000)
				return
			}

			shortcutSequence.current = []
		}
		const up = (e: KeyboardEvent) => {
			if (["Alt", "Control", "Meta"].includes(e.key)) {
				modifierKeyDown.current = false
			}
		}
		const resetModifiers = () => {
			modifierKeyDown.current = false
			shortcutSequence.current = []
		}

		document.addEventListener("keydown", down)
		document.addEventListener("keyup", up)
		window.addEventListener("blur", resetModifiers)
		return () => {
			document.removeEventListener("keydown", down)
			document.removeEventListener("keyup", up)
			window.removeEventListener("blur", resetModifiers)
			if (shortcutTimeout.current) clearTimeout(shortcutTimeout.current)
		}
	}, [])

	return (
		<CommandDialog
			open={open}
			onOpenChange={setOpen}
			commandProps={{ shouldFilter: false }}
		>
			<CommandInput
				placeholder="Type a command or search..."
				value={search}
				onValueChange={setSearch}
			/>
			<CommandList>
				<CommandEmpty>
					{deferredSearch.trim().length < 2
						? "Type at least 2 characters to search."
						: searchQuery.isFetching
							? "Searching..."
							: "No results found."}
				</CommandEmpty>
				{visibleActions.length > 0 && (
					<CommandGroup heading="Actions">
						{visibleActions.map((action) => {
							const Icon = action.icon
							const shortcut = quickNavigationShortcuts[action.shortcutId]
							return (
								<CommandItem
									key={action.id}
									value={`${action.title} ${displayShortcut(shortcut.keys)} ${shortcut.keywords.join(" ")}`}
									disabled={action.disabled}
									onSelect={() => runAction(action)}
								>
									<Icon className="text-text-main size-4" />
									<span>{action.title}</span>
									<CommandShortcut>
										{displayShortcut(shortcut.keys)}
									</CommandShortcut>
								</CommandItem>
							)
						})}
					</CommandGroup>
				)}
				{resultGroups.map((type) => {
					const results = resultsByType.get(type) ?? []
					if (!results.length) return null
					const Icon = resultIcons[type]
					return (
						<CommandGroup key={type} heading={enumFormat(type)}>
							{results.map((result) => (
								<CommandItem
									key={`${result.type}:${result.id}`}
									value={`${result.type} ${result.title} ${result.subtitle ?? ""} ${result.excerpt ?? ""}`}
									onSelect={() => {
										if (posthogEnabled && posthog) {
											posthog.capture("global_search_result_selected", {
												queryLength: deferredSearch.trim().length,
												resultType: result.type,
												resultRank: result.rank,
												organizationId: organization?.id,
												teamId: team?.id
											})
										}
										setOpen(false)
										navigate(result.url)
									}}
									className="items-start"
								>
									<Icon className="mt-0.5 size-4 text-icons" />
									<div className="min-w-0">
										<div className="truncate font-medium">{result.title}</div>
										{result.subtitle && (
											<div className="truncate text-xs text-text-sub">
												{result.subtitle}
											</div>
										)}
										{result.excerpt && (
											<div className="line-clamp-1 text-xs text-text-small">
												{result.excerpt}
											</div>
										)}
									</div>
								</CommandItem>
							))}
						</CommandGroup>
					)
				})}
			</CommandList>
		</CommandDialog>
	)
}
